
if (!customElements.get("sticky-atc")) {
  customElements.define(
    "sticky-atc",
    class StickyAtc extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          prodTitle: ".m-sticky-addtocart--title",
          mainImage: ".m-sticky-addtocart--image",
          addToCart: ".m-add-to-cart",
          buyNowBtn: ".m-product-dynamic-checkout",
          variantIdSelect: '[name="id"]',
          foxkitBtn: ".foxkit-button",
          select: "select",
          mobilePrice: ".m-sticky-addtocart--mobile-price",
          currentPrice: ".sticky-atc-current-price",
          comparePrice: ".sticky-atc-compare-price",
        };

        this.hasCustomFields = !!document.querySelector(
          ".m-main-product--info .m-product-custom-field"
        );
      }

      connectedCallback() {
        this.mainProduct = document.querySelector(
          "product-info[id^='MainProduct']"
        );

        this.container = this.closest(".m-sticky-addtocart");

        this.mainProductForm = document.querySelector(
          ".m-product-form--main"
        );

        this.mainProductInfo = document.querySelector(
          ".m-main-product--info"
        );

        this.mainAddToCart = this.mainProductForm
          ? this.mainProductForm.querySelector(".m-add-to-cart")
          : null;

        this.mainDynamicCheckout = this.mainProductForm
          ? this.mainProductForm.querySelector(
              this.selectors.buyNowBtn
            )
          : null;

        this.disableSelectedVariantDefault =
          this.mainProduct?.dataset.disableSelectedVariantDefault === "true";

        this.domNodes = queryDomNodes(
          this.selectors,
          this.container
        );

        this.init();
      }

      init() {
        if (!this.mainAddToCart || !this.mainProductForm) {
          this.container.style.setProperty("--m-translate-y", 0);
          return;
        }

        this.variantData = this.getVariantData();

        this.mobilePrice = this.querySelector(
          this.selectors.mobilePrice
        );

        this.currentPriceElement = this.querySelector(
          this.selectors.currentPrice
        );

        this.comparePriceElement = this.querySelector(
          this.selectors.comparePrice
        );

        const headerHeight = MinimogSettings.headerHeight || 66;
        const rootMargin = `${headerHeight}px 0px 0px 0px`;

        if ("IntersectionObserver" in window) {
          this.observer = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.intersectionRatio !== 1) {
                  this.container.style.setProperty(
                    "--m-translate-y",
                    0
                  );
                } else {
                  this.container.style.setProperty(
                    "--m-translate-y",
                    "100%"
                  );
                }

                document.documentElement.classList[
                  entry.intersectionRatio !== 1 ? "add" : "remove"
                ]("stick-atc-show");
              });
            },
            {
              threshold: 1,
              rootMargin,
            }
          );
        }

        if (this.disableSelectedVariantDefault) {
          this.handleDisableSelectedVariantDefault();
        }

        const { prodTitle, mainImage } = this.domNodes;

        if (prodTitle) {
          prodTitle.addEventListener("click", () => __scrollToTop());
        }

        if (mainImage) {
          mainImage.addEventListener("click", () => __scrollToTop());
        }

        this.handleCustomFields();

        this.setObserveTarget();
        this.syncWithMainProductForm();

        // Initialize mobile price
        const selectedVariantId = this.querySelector(
          this.selectors.variantIdSelect
        )?.value;

        if (selectedVariantId) {
          const selectedVariant = this.variantData.find(
            (variant) => variant.id === Number(selectedVariantId)
          );

          if (selectedVariant) {
            this.updateStickyPrice(selectedVariant);
          }
        }

        const mql = window.matchMedia(
          MinimogTheme.config.mediaQueryMobile
        );

        mql.onchange = this.setStickyAddToCartHeight.bind(this);

        this.setStickyAddToCartHeight();

        // Sticky variant selector
        const stickyVariantSelect = this.querySelector(
          this.selectors.variantIdSelect
        );

        if (stickyVariantSelect) {
          stickyVariantSelect.addEventListener("change", (e) => {
            const { target } = e;

            const selectedVariantId = target.value;

            this.currentVariant = this.variantData.find(
              (variant) =>
                variant.id === Number(selectedVariantId)
            );

            if (this.currentVariant) {
              this.updateStickyPrice(this.currentVariant);

              this.toggleAddButton(
                !this.currentVariant.available,
                window.MinimogStrings.soldOut
              );
            } else {
              this.updateStickyPrice(null);

              this.toggleAddButton(
                true,
                window.MinimogStrings.unavailable
              );
            }

            const variantPicker = this.mainProduct
              ? this.mainProduct.querySelector("variant-picker")
              : null;

            const selectedOption =
              target.options[target.selectedIndex];

            const selectedOptionIds = selectedOption?.dataset.options
              ? selectedOption.dataset.options
                  .split(",")
                  .filter((id) => id)
              : [];

            if (variantPicker) {
              selectedOptionIds.forEach((optionId) => {
                const input = variantPicker.querySelector(
                  `[data-option-value-id="${optionId}"]`
                );

                if (!input) return;

                const { tagName } = input;

                switch (tagName) {
                  case "OPTION": {
                    const inputParent = input.parentNode;
                    inputParent.value = input.value;
                    break;
                  }

                  case "INPUT":
                    input.checked = true;
                    break;
                }
              });

              variantPicker.dispatchEvent(
                new Event("change", { bubbles: true })
              );
            }
          });
        }
      }

      // GET VARIANT DATA
      getVariantData() {
        this.variantData =
          this.variantData ||
          JSON.parse(
            this.container.querySelector(
              '[type="application/json"]'
            ).textContent
          );

        return this.variantData;
      }

      // FORMAT MONEY
      formatMoney(cents) {
        const moneyFormat =
          this.mobilePrice?.dataset.moneyFormat ||
          window.Shopify?.money_format ||
          window.Shopify?.moneyFormat;

        if (
          typeof window.Shopify?.formatMoney === "function" &&
          moneyFormat
        ) {
          return window.Shopify.formatMoney(
            cents,
            moneyFormat
          );
        }

        const currency =
          window.Shopify?.currency?.active ||
          "INR";

        return new Intl.NumberFormat(
          document.documentElement.lang || "en-IN",
          {
            style: "currency",
            currency: currency,
          }
        ).format(cents / 100);
      }

      // UPDATE MOBILE PRICE AND COMPARE PRICE
      updateStickyPrice(variant) {
        if (
          !this.currentPriceElement ||
          !this.comparePriceElement
        ) {
          return;
        }

        if (!variant) {
          this.currentPriceElement.textContent = "";
          this.comparePriceElement.textContent = "";
          this.comparePriceElement.hidden = true;
          return;
        }

        // Update selling price
        this.currentPriceElement.innerHTML = this.formatMoney(
          variant.price
        );

        // Update compare-at price
        if (
          variant.compare_at_price &&
          variant.compare_at_price > variant.price
        ) {
          this.comparePriceElement.innerHTML = this.formatMoney(
            variant.compare_at_price
          );

          this.comparePriceElement.hidden = false;
        } else {
          this.comparePriceElement.textContent = "";
          this.comparePriceElement.hidden = true;
        }
      }

      // OBSERVE MAIN PRODUCT FORM
      setObserveTarget() {
        if (this.observer && this.mainProductForm) {
          this.observer.observe(this.mainProductForm);
          this.observeTarget = this.mainProductForm;
        }
      }

      // SET UNAVAILABLE
      setUnavailable() {
        const productForm = this.querySelector(".m-product-form");

        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');

        if (addButton) {
          this.toggleAddButton(
            true,
            window.MinimogStrings.unavailable
          );
        }
      }

      // TOGGLE ADD TO CART BUTTON
      toggleAddButton(
        disable = true,
        text,
        modifyClass = true
      ) {
        const productForm = this.querySelector(".m-product-form");

        if (!productForm) return;

        const addButton = productForm.querySelector('[name="add"]');

        const addButtonText = productForm.querySelector(
          '[name="add"] > span.m-add-to-cart--text'
        );

        if (!addButton) return;

        if (disable) {
          addButton.setAttribute("disabled", "disabled");

          if (text && addButtonText) {
            addButtonText.textContent = text;
          }
        } else {
          addButton.removeAttribute("disabled");

          if (addButtonText) {
            addButtonText.innerHTML =
              window.MinimogStrings.addToCart;
          }
        }

        if (!modifyClass) return;
      }

      // DISABLE DEFAULT VARIANT
      handleDisableSelectedVariantDefault() {
        const urlParams = new URLSearchParams(
          window.location.search
        );

        if (urlParams.has("variant")) return;

        const pickerFields = this.querySelector(
          ".m-product-option--dropdown-select"
        );

        if (pickerFields) {
          pickerFields.value = "";
          this.setUnavailable();
          this.updateStickyPrice(null);
        }
      }

      // STICKY HEIGHT
      setStickyAddToCartHeight() {
        document.documentElement.style.setProperty(
          "--f-sticky-atc-bar-height",
          this.offsetHeight + "px"
        );

        window.MinimogSettings.stickyAddToCartHeight =
          this.offsetHeight;
      }

      // SYNC WITH MAIN PRODUCT FORM
      syncWithMainProductForm() {
        const variantInput = this.querySelector('[name="id"]');

        MinimogEvents.subscribe(
          MinimogTheme.pubSubEvents.variantChange,
          (e) => {
            const isMainProduct =
              e.data.sectionId ===
              this.mainProduct.dataset.sectionId;

            if (!isMainProduct) return;

            this.currentVariant = e.data.variant;

            if (this.currentVariant) {
              variantInput.value = this.currentVariant.id;

              // UPDATE MOBILE PRICE
              this.updateStickyPrice(this.currentVariant);

              this.toggleAddButton(
                !this.currentVariant.available,
                window.MinimogStrings.soldOut
              );
            } else {
              variantInput.value = "";

              // CLEAR PRICE WHEN VARIANT IS UNAVAILABLE
              this.updateStickyPrice(null);

              this.toggleAddButton(
                true,
                window.MinimogStrings.unavailable
              );
            }
          }
        );
      }

      // CUSTOM FIELDS
      handleCustomFields() {
        if (!this.hasCustomFields) return;

        const { addToCart, buyNowBtn, foxkitBtn } =
          this.domNodes;

        let hasCustomFieldRequired = false;

        const customFields = document.querySelectorAll(
          ".m-main-product--info .m-product-custom-field"
        );

        customFields.forEach((item) => {
          const field = item.querySelector(".form-field");

          if (
            field &&
            field.value === "" &&
            field.hasAttribute("required")
          ) {
            hasCustomFieldRequired = true;
          }
        });

        if (hasCustomFieldRequired && addToCart) {
          addToCart.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            __scrollToTop(
              this.mainProductInfo,
              () => this.mainAddToCart.click()
            );
          });
        }

        if (buyNowBtn) {
          buyNowBtn.addEventListener(
            "click",
            (e) => {
              const missing = validateForm(
                this.mainProductInfo
              );

              if (missing.length > 0) {
                e.preventDefault();
                e.stopPropagation();

                __scrollToTop(
                  this.mainProductInfo,
                  () => this.mainDynamicCheckout.click()
                );
              }
            },
            true
          );
        }

        if (foxkitBtn) {
          foxkitBtn.addEventListener("click", (e) => {
            e.preventDefault();
            e.stopPropagation();

            __scrollToTop(
              this.mainProductInfo,
              () => this.mainAddToCart.click()
            );
          });
        }
      }
    }
  );
}
